import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, List, ListItem, ListItemText, Collapse } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

const useStyles = makeStyles((theme) => ({
    sidebarContainer: {
        width: '280px',
        flexShrink: 0,
        position: 'sticky',
        top: theme.spacing(3),
        maxHeight: 'calc(100vh - 48px)',
        overflowY: 'auto',
        paddingLeft: theme.spacing(2),
        borderLeft: `1px solid ${theme.palette.divider}`,
        [theme.breakpoints.down('sm')]: {
            position: 'relative',
            width: '100%',
            maxHeight: 'none',
            marginBottom: '1em',
            top: 0,
        },
    },
    tocTitle: {
        fontWeight: 'bold !important',
        marginBottom: theme.spacing(1),
        fontSize: '1rem',
        color: theme.palette.text.secondary,
    },
    tocList: {
        padding: 0,
        listStylePosition: 'inside',
    },
    tocItem: {
        padding: 0,
        paddingLeft: 4,
        paddingRight: 4,
        margin: 0,
        marginTop: "0.2em",
        marginBottom: "0.2em",
        borderLeft: '2px solid transparent',
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'flex-start',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    tocItemLevel1: { paddingLeft: "8px", fontWeight: 'bold' },
    tocItemLevel2: { paddingLeft: "24px" },
    tocItemLevel3: { paddingLeft: "32px" },
    activeTocItem: {
        borderLeftColor: theme.palette.primary.main,
        backgroundColor: theme.palette.action.selected,
        '& .MuiListItemText-primary': {
            color: theme.palette.primary.secondary,
            fontWeight: "bold"
        },
    },
    tocText: {
        paddingLeft: "0.2em",
        margin: 0,
        '&:hover': {
            textDecoration: 'none',
        },
    },
    expandIcon: {
        display: 'flex',
        cursor: 'pointer',
        color: theme.palette.text.secondary,
        marginLeft: 'auto',
    }
}));

function TocListItem({ heading, activeId, onClick, minLevel, autoNumber, index, hasChildren, toggleSection, isOpen, section }) {
    const level = (parseInt(heading.tagName.substring(1), 10) || 2) - minLevel;
    const classes = useStyles();
    const meRef = useRef(null);
    const isActive = activeId === heading.id;

    return (
        <ListItem
            ref={meRef}
            button
            dense
            onClick={(e) => onClick(e, heading.id)}
            component='a'
            href={"#" + heading.id}
            className={`${classes.tocItem} ${classes["tocItemLevel" + Math.min(level, 3)]} ${isActive ? classes.activeTocItem : ''}`}
        >
            {autoNumber && (
                <Typography component="span" variant="body2" style={{ marginRight: '6px', fontWeight: isActive ? 'bold' : 'normal' }}>
                    {index}
                </Typography>
            )}
            <ListItemText
                primary={heading.text}
                style={{ margin: 0 }}
                classes={{ primary: classes.tocText }}
                onClick={e => e.preventDefault()}
            />
            {hasChildren && (
                <Box
                    className={classes.expandIcon}
                    onClick={(e) => toggleSection(e, section.id)}
                >
                    {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </Box>
            )}
        </ListItem>
    );
}

function buildHeadingTree(headings, minLevel) {
    const tree = [];
    let currentParent = null;

    headings.forEach((heading) => {
        const level = (parseInt(heading.tagName.substring(1), 10) || 2) - minLevel;

        if (level === 0 || !currentParent) {
            currentParent = { ...heading, children: [] };
            tree.push(currentParent);
        } else {
            currentParent.children.push(heading);
        }
    });

    return tree;
}

export default function TableOfContentsSidebar({ headings, autoNumber }) {
    const classes = useStyles();
    const [activeId, setActiveId] = useState('');
    const [openSections, setOpenSections] = useState({});
    const isScrollingRef = useRef(false);
    const scrollTimeoutRef = useRef(null);

    const minLevel = headings && headings.length > 0
        ? Math.min(...headings.map(h => parseInt(h.tagName.substring(1), 10) || 2))
        : 2;

    const headingTree = buildHeadingTree(headings || [], minLevel);

    useEffect(() => {
        if (!headings || headings.length === 0) return;
        const callback = (entries) => {
            if (isScrollingRef.current) return;

            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setActiveId(entry.target.id);
                }
            });
        };

        const observer = new IntersectionObserver(callback, {
            rootMargin: '-80px 0px -40% 0px',
            threshold: 0.1,
        });

        headings.forEach((h) => {
            const el = document.getElementById(h.id);
            if (el) observer.observe(el);
        });

        return () => {
            observer.disconnect();
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        };

    }, [headings]);

    const handleLinkClick = (e, id) => {
        const el = document.getElementById(id);
        if (el) {
            isScrollingRef.current = true;
            setActiveId(id);
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });

            const unlockScroll = () => {
                isScrollingRef.current = false;
                window.removeEventListener('scrollend', unlockScroll);
                if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
            };
            window.addEventListener('scrollend', unlockScroll, { once: true });
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = setTimeout(() => {
                if (isScrollingRef.current) {
                    unlockScroll();
                }
            }, 1500);
            setTimeout(() => { if (isScrollingRef.current) unlockScroll(); }, 1500);
        }
    };

    const toggleSection = (e, parentId) => {
        e.stopPropagation();
        e.preventDefault();
        setOpenSections(prev => ({ ...prev, [parentId]: !prev[parentId] }));
    };

    if (!headings || headings.length === 0) return null;

    return (
        <nav className={classes.sidebarContainer} aria-label="Table of Contents">
            <Typography className={classes.tocTitle} id="toc">Contents</Typography>
            <List className={classes.tocList} component="div">
                {headingTree.logo || headingTree.map((section, index) => {
                    const isOpen = !!openSections[section.id];
                    const hasChildren = section.children && section.children.length > 0;
                    const isParentActive = hasChildren && section.children.some(c => activeId === c.id);

                    return (
                        <React.Fragment key={section.id}>
                            <Box alignItems="flex-start" display="flex" style={{ marginBottom: "0", marginTop: "0.2em" }}>
                                <Box width="100%">
                                    <TocListItem
                                        minLevel={minLevel}
                                        heading={section}
                                        hasChildren={hasChildren}
                                        isOpen={isOpen}
                                        toggleSection={toggleSection}
                                        section={section}
                                        activeId={!isOpen && isParentActive ? section.id : activeId}
                                        autoNumber={autoNumber}
                                        index={`${index + 1}.`}
                                        onClick={hasChildren ? (e) => { toggleSection(e, section.id); handleLinkClick(e, section.id); } : handleLinkClick}
                                    />
                                </Box>
                                
                            </Box>
                            {hasChildren && (
                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding style={{ borderLeft: "1px dashed black", marginLeft: "1em" }} >
                                        {section.children.map((child, childIndex) => (
                                            <TocListItem
                                                minLevel={minLevel}
                                                key={child.id}
                                                heading={child}
                                                activeId={activeId}
                                                autoNumber={autoNumber}
                                                index={`${index + 1}.${childIndex + 1}`}
                                                onClick={handleLinkClick}
                                            />
                                        ))}
                                    </List>
                                </Collapse>
                            )}
                        </React.Fragment>
                    );
                })}
            </List>
        </nav>
    );
}